import type { AttributeType } from '~/types/plugins/subscriptions';
import { cycle, detailPrice, priceCycle } from '../api/utils';

export const useSubscription = () => {
    const nuxtApp = useNuxtApp();
    const { $i18n, $globals, $t } = nuxtApp;

    function getName(
        item: { id: number; name: string; attributes: AttributeType[] },
        gotTxtName = false
    ) {
        const htmlName: string | undefined =
            item &&
            item['attributes']?.find((i: AttributeType) => {
                return (
                    i.key === 'DISPLAY_SERVICE_NAME_HTML_TEXT_' + ( $i18n.locale.value || $i18n.locale )
                );
            })?.value;

        let name;
        if (gotTxtName || !htmlName) {
            name =
                item &&
                item['attributes']?.find((i: AttributeType) => {
                    return i.key === 'DISPLAY_SERVICE_NAME_TEXT_' + ( $i18n.locale.value || $i18n.locale );
                })?.value;
        }
        const subName = name || item?.name;
        return (gotTxtName ? subName : htmlName || subName) || '';
    }
    function getIcon(item: { id: number | string; attributes: AttributeType[] }): string {
        let img: string = '';
        let imgAltTheme: string = '';
        item['attributes']?.forEach((attr) => {
            if (
                $globals.alternatTheme &&
                attr.key == 'ment_icon_alernativeTheme'
            ) {
                imgAltTheme = attr.value;
            } else if (attr.key == 'ment_icon') {
                img = attr.value;
            }
        });
        return imgAltTheme || img;
    }
    function getBackgroundImage(item: { id: number; attributes: AttributeType[] }) {
        const image =
            item &&
            item['attributes']?.find((img) => {
                return img.key === ('ment_background_image' as String);
            })?.value;
        return image;
    }
    function getDescription(item: { id: number; attributes: AttributeType[] }) {
        const description =
            item &&
            item['attributes']?.find((i) => {
                return i.key === 'DISPLAY_SERVICE_INFO_TEXT_' +( $i18n.locale?.value||$i18n.locale);
            })?.value;
        return description;
    }
    function getPrice(item: any) {
        const itemPrice = item?.subscriptionTypePrice || item?.price || item?.subscriptionTypePromotionPrice;
        const price: {
            currency: string;
            amountMain: number;
            amountExt: number;
        } = detailPrice(itemPrice, item?.attributes, nuxtApp) as any;
        return price;
    }
    function getCycle(item: string) {
        return $t(priceCycle(item));
    }
    function getBillingCycle(item: string) {
        return $t(cycle(item));
    }
    function getUrl(item: { id: ''; attributes: AttributeType[] }) {
        const url =
            item &&
            item['attributes']?.find((i) => {
                return i.key === ('ment_url' as String);
            })?.value;
        return url;
    }
    function getBestValue(item: { attributes: AttributeType[] }) {
        return item?.attributes?.find((note: { [key: string]: string }) => {
            return note.key === 'ment_best_value';
        })?.value;
    }
    function getLongDescription(
        item: { id: ''; attributes: AttributeType[] },
        forceCreditDesc?: boolean
    ) {
        const longDescription =
            item &&
            ((forceCreditDesc &&
                item['attributes']?.find((i) => {
                    return (
                        i.key === 'DISPLAY_SERVICE_DESC_TEXT_CREDIT_' + $i18n.locale
                    );
                })?.value) ||
                item['attributes']?.find((i) => {
                    return i.key === 'DISPLAY_SERVICE_DESC_TEXT_' + $i18n.locale;
                })?.value);
        return longDescription;
    }
    function getBulletDescription(item: {
        id: '';
        attributes: AttributeType[];
    }) {
        const bulletDescription =
            item &&
            item['attributes']?.find((i) => {
                return i.key === 'DISPLAY_SERVICE_BULLETS_TEXT_' + $i18n.locale.value;
            })?.value;
        return bulletDescription ? JSON.parse(bulletDescription) : [];
    }
    function getPurchaseDescription(item: {
        id: '';
        attributes: AttributeType[];
    }) {
        const purchaseDescription =
            item &&
            item['attributes']?.find((i) => {
                return (
                    i.key ===
                    'DISPLAY_SERVICE_ABOVE_PURCHASE_OPTIONS_' + $i18n.locale
                );
            })?.value;
        return purchaseDescription;
    }
    function getTermsAndCond(item: { id: ''; attributes: AttributeType[] }) {
        const termsAndCond =
            item &&
            item['attributes']?.find((i) => {
                return i.key === 'DISPLAY_SERVICE_ABOVE_CTA_' + $i18n.locale;
            })?.value;
        return termsAndCond;
    }
    function getTitle(item: { id: string | number; attributes: AttributeType[] }) {
        const title =
            item &&
            item['attributes']?.find((i) => {
                return i.key === 'DISPLAY_SERVICE_TITLE_TEXT_' + $i18n.locale.value;
            })?.value;
        return title;
    }

    function getTermsAndCondForPopup(sub: any) {
        let item = sub.flat();
        if (!item.length && Array.isArray(item) && item.length > 1) {
          return null;
        } else if (Array.isArray(item) && item.length == 1) {
          item = item[0];
        }
        const termsAndCond =
          item &&
          item['attributes']?.find((i: any) => {
            return i.key === 'DISPLAY_SERVICE_TNC_DATA_POPUP';
          })?.value;
        try {
          const Tnc = termsAndCond && JSON.parse(termsAndCond);
          if (Tnc) {
            Tnc.text = $t(Tnc.text);
          }
          return Tnc;
        } catch (error) {
          // skip error
        }
    }

    function getLocalizedServiceName(item: {
      id: string | number;
      attributes: AttributeType[];
    }) {
      let title =
        item &&
        item['attributes']?.find((i) => {
          return i.key === 'DISPLAY_SERVICE_NAME_' + $i18n.locale.value;
        })?.value;
      return title;
    }

    return {
      getName,
      getIcon,
      getBackgroundImage,
      getDescription,
      getPrice,
      getCycle,
      getBillingCycle,
      getUrl,
      getBestValue,
      getLongDescription,
      getBulletDescription,
      getPurchaseDescription,
      getTermsAndCond,
      getTitle,
      getTermsAndCondForPopup,
      getLocalizedServiceName,
    };
}